import { Icon, Tooltip } from "@material-ui/core";
import toBlob from "canvas-to-blob";
import React from "react";
import Dropzone from "react-dropzone";
import i18n from "Translations/i18n";
import { MediaOrientationUtil } from "Utils/MediaOrientationUtil";
import styles from "./MediaUpload.scss";

interface IProps {
    id?: string;
    imageUrl?: string;
    videoUrl?: string;
    videoThumbnailUrl?: string;
    onChange?: (state: { imageFile?: File, videoFile?: File, videoThumbnailFile?: File }) => void;
    isImageUploadOnly?: boolean;
}

interface IState {
    imageUrl?: string;
    videoUrl?: string;
    videoThumbnailUrl?: string;
    imageFile?: File;
    videoFile?: File;
    videoThumbnailFile?: File;
    isPlayingVideoPreview: boolean;
}

class MediaUpload extends React.Component<IProps, IState> {
    public videoPlayerRef = React.createRef<HTMLVideoElement>();

    constructor(props: IProps) {
        super(props);

        this.state = {
            imageUrl: props.imageUrl,
            videoUrl: props.videoUrl,
            videoThumbnailUrl: props.videoThumbnailUrl,
            isPlayingVideoPreview: false
        };

        this.delete = this.delete.bind(this);
        this.playPauseVideoPreview = this.playPauseVideoPreview.bind(this);
        this.getCurrentFrame = this.getCurrentFrame.bind(this);
        this.updateImageUrl = this.updateImageUrl.bind(this);

        toBlob.init();
    }

    private isVideo(file: File) {
        return file.type.startsWith("video/");
    }

    public async onDropAsync(files: File[], isThumb: boolean = false) {
        if (files && files.length > 0) {
            const file = files[0];
            if (!isThumb) {
                if (this.isVideo(file)) {
                    this.setState({
                        imageUrl: undefined,
                        videoThumbnailUrl: undefined,
                        videoUrl: URL.createObjectURL(file),
                        videoFile: file,
                        isPlayingVideoPreview: false
                    });
                    if (this.props.onChange) {
                        this.props.onChange({ videoFile: file, videoThumbnailFile: undefined });
                    }
                }
                else {
                    if (file.type.indexOf("jpeg") !== -1) {
                        // Rotate the image if needed.
                        this.correctExifOrientation(file);
                    }
                    else {
                        this.updateImageUrl(file);
                    }
                }
            }
            else {
                this.setState({
                    imageUrl: undefined,
                    videoThumbnailUrl: URL.createObjectURL(file),
                    videoThumbnailFile: file
                });

                if (this.props.onChange) {
                    this.props.onChange({ videoThumbnailFile: file, videoFile: this.state.videoFile });
                }
            }
        }
    }

    private correctExifOrientation(file: File) {
        MediaOrientationUtil.rotateImageFile(file).then((newFile: File) => {
            this.updateImageUrl(newFile);
        });
    }

    private updateImageUrl(file: File) {
        this.setState({
            imageUrl: URL.createObjectURL(file),
            videoUrl: undefined,
            videoThumbnailUrl: undefined,
            imageFile: file
        });

        if (this.props.onChange) {
            this.props.onChange({ imageFile: file });
        }
    }

    public delete() {
        this.setState({
            imageUrl: undefined,
            videoUrl: undefined,
            videoThumbnailUrl: undefined,
            imageFile: undefined,
            videoFile: undefined,
            videoThumbnailFile: undefined
        });
        if (this.props.onChange) {
            this.props.onChange({ imageFile: undefined, videoFile: undefined, videoThumbnailFile: undefined });
        }
    }

    public playPauseVideoPreview() {
        const videoPreview = this.videoPlayerRef.current!;
        if (this.state.isPlayingVideoPreview) {
            videoPreview.pause();
            this.setState({ isPlayingVideoPreview: false });
        }
        else {
            videoPreview.play();
            this.setState({ isPlayingVideoPreview: true });
        }
    }

    public getCurrentFrame() {
        const videoPreview = this.videoPlayerRef.current!;
        const canvas = document.createElement("canvas");
        canvas.width = videoPreview.videoWidth;
        canvas.height = videoPreview.videoHeight;
        const canvasContext = canvas.getContext("2d");
        canvasContext!.drawImage(videoPreview, 0, 0);

        canvas.toBlob((blob) => {
            if (blob) {
                const blobFile: any = blob;
                blobFile.name = "thumbnail.png";
                blobFile.lastModified = new Date();
                const file: File = blobFile;

                this.setState({
                    imageUrl: undefined,
                    videoThumbnailUrl: URL.createObjectURL(file),
                    videoThumbnailFile: file
                });
                if (this.props.onChange) {
                    this.props.onChange({ videoThumbnailFile: file, videoFile: this.state.videoFile });
                }
            }
        });
    }
    
    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> {
        const partialState: Partial<IState> = {};

        if (nextProps.imageUrl !== prevState.imageUrl) {
            partialState.imageUrl = nextProps.imageUrl;
        }

        if (nextProps.videoThumbnailUrl !== prevState.videoThumbnailUrl) {
            partialState.videoThumbnailUrl = nextProps.videoThumbnailUrl;
        }

        if (nextProps.videoUrl !== prevState.videoUrl) {
            partialState.videoUrl = nextProps.videoUrl;
        }

        return partialState;
    }

    public render() {
        const uploadInputStyle = { display: "block", width: 0, height: 0, opacity: 0 };

        return (
            <div id={`${this.props.id}Picture`} className={`${styles.mediaUpload} ${this.state.imageUrl ? null : styles.noImageSelected}`} style={{ "backgroundImage": this.state.imageUrl ? `url(${this.state.imageUrl})` : null }}>
                {
                    this.state.videoUrl ?
                        <video id="videoPreview" ref={this.videoPlayerRef} src={this.state.videoUrl} className={styles.videoPreview} autoPlay={false} crossOrigin="Anonymous" /> :
                        null
                }
                <Dropzone
                    accept={this.props.isImageUploadOnly ? "image/*" : "image/*,video/*"}
                    disableClick={true}
                    onDrop={files => this.onDropAsync(files)}
                    multiple={false}
                >
                    {({ getRootProps, getInputProps, isDragAccept, isDragReject, open }) => (
                        <div {...getRootProps({ className: styles.dropzone })} className={`${styles.overlay} ${isDragAccept ? styles.dragAccept : null} ${isDragReject ? styles.dragReject : null}`}>
                            <input {...getInputProps({ id: this.props.id, type: "file" })} style={uploadInputStyle} />

                            {
                                this.state.videoUrl ?
                                    <div id="btnPlay" onClick={this.playPauseVideoPreview} className={styles.attachIcon}>
                                        <Tooltip
                                            title={this.state.isPlayingVideoPreview ? i18n.t("tasks.media.pause") : i18n.t("tasks.media.play")}
                                            aria-label={this.state.isPlayingVideoPreview ? i18n.t("tasks.media.pause") : i18n.t("tasks.media.play")}>
                                            <Icon className={styles.icon}>
                                                {this.state.isPlayingVideoPreview ? "pause" : "play_arrow"}
                                            </Icon>
                                        </Tooltip>
                                    </div> :
                                    null
                            }
                            <Tooltip title={i18n.t("tasks.media.selectFile")} aria-label={i18n.t("tasks.media.selectFile")}>
                                <div onClick={open} className={styles.attachIcon}><Icon className={styles.icon}>attach_file</Icon></div>
                            </Tooltip>
                            {
                                this.state.videoUrl || this.state.imageUrl ?
                                    <Tooltip id="btnDelete" title={i18n.t("common.delete")} aria-label={i18n.t("common.delete")}>
                                        <div onClick={this.delete} className={styles.attachIcon}><Icon className={styles.icon}>delete</Icon></div>
                                    </Tooltip> :
                                    null
                            }
                        </div>
                    )}
                </Dropzone>
                {
                    this.state.videoUrl && !this.state.isPlayingVideoPreview ?
                        <div className={`${styles.thumb} ${this.state.videoThumbnailUrl ? styles.file_selected : null}`} style={{ "backgroundImage": `url(${this.state.videoThumbnailUrl})` }}>
                            <Dropzone
                                accept={"image/*"}
                                disableClick={true}
                                onDrop={files => this.onDropAsync(files, true)}
                                multiple={false}
                            >
                                {({ getRootProps, getInputProps, isDragAccept, isDragReject, open }) => (
                                    <div {...getRootProps({ className: styles.dropzone })} className={`${styles.overlay} ${isDragAccept ? styles.dragAccept : null} ${isDragReject ? styles.dragReject : null}`}>
                                        <input {...getInputProps({ id: `${this.props.id}Thumbnail`, type: "file" })} style={uploadInputStyle} />
                                        <Tooltip id="btnSelectThumbnail" title={i18n.t("tasks.media.selectThumbnail")} aria-label={i18n.t("tasks.media.selectThumbnail")}>
                                            <div onClick={open} className={styles.attachIcon}><Icon className={styles.icon}>attach_file</Icon></div>
                                        </Tooltip>
                                        <Tooltip id="btnCaptureThumbnail" title={i18n.t("tasks.media.captureFrame")} aria-label={i18n.t("tasks.media.captureFrame")}>
                                            <div onClick={this.getCurrentFrame} className={styles.attachIcon}><Icon className={styles.icon}>crop_free</Icon></div>
                                        </Tooltip>
                                    </div>
                                )}
                            </Dropzone>
                        </div> :
                        null
                }
            </div>
        );
    }
}

export default MediaUpload;


// WEBPACK FOOTER //
// ./src/Components/Common/MediaUpload/MediaUpload.tsx